import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import {
    Box,
    Typography,
    Button,
    TextField,
    InputAdornment,
    IconButton,
    Chip,
    CircularProgress,
    ToggleButtonGroup,
    ToggleButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
} from '@mui/material';
import {
    Search as SearchIcon,
    Dashboard as DashboardIcon,
    ArrowBack as ArrowBackIcon,
    Save as SaveIcon,
    LockOpen as LockOpenIcon,
    Lock as LockIcon,
} from '@mui/icons-material';
import { AdminPageContainer } from '../../components/admin/sharedStyles';
import PageHeader from '../../components/PageHeader';
import DashboardEditor from '../../components/DashboardEditor';
import { getRoles, updateRole, getRoleExpandedPermissions } from '../../redux/ducks/role';
import { showAlert } from '../../redux/slices/alerts';

const FiltersLayout = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
}));

const SearchInput = styled(TextField)({
    width: '100%',
});

const LoadingRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const EmptyState = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const EditorActions = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
}));

const InfoNote = styled(Alert)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const HasDefaultBadge = ({ count }) => (
    <Chip
        label={count > 0 ? `${count} widgets` : 'Empty'}
        size="small"
        color={count > 0 ? 'success' : 'default'}
        variant="outlined"
    />
);

const RoleListView = ({ roles, loading, searchTerm, setSearchTerm, typeFilter, setTypeFilter, onEdit }) => (
    <>
        <PageHeader
            title="Role Default Dashboards"
            subtitle="Build the dashboard each role sees before they personalize their own."
            filters={
                <FiltersLayout>
                    <SearchInput
                        fullWidth
                        placeholder="Search roles by name or description..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <ToggleButtonGroup
                        value={typeFilter}
                        exclusive
                        onChange={(e, newValue) => { if (newValue !== null) setTypeFilter(newValue); }}
                        size="small"
                    >
                        <ToggleButton value="all">All Types</ToggleButton>
                        <ToggleButton value="Internal">Internal</ToggleButton>
                        <ToggleButton value="General">General</ToggleButton>
                    </ToggleButtonGroup>
                </FiltersLayout>
            }
        />

        {loading ? (
            <LoadingRow><CircularProgress /></LoadingRow>
        ) : roles.length === 0 ? (
            <EmptyState>
                <Typography variant="body1" color="text.secondary">No roles match your filters.</Typography>
            </EmptyState>
        ) : (
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>Role</strong></TableCell>
                            <TableCell><strong>Type</strong></TableCell>
                            <TableCell align="center"><strong>Level</strong></TableCell>
                            <TableCell align="center"><strong>Default Dashboard</strong></TableCell>
                            <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {roles.map((role) => {
                            const widgetCount = Array.isArray(role.defaultDashboard?.layout)
                                ? role.defaultDashboard.layout.length
                                : 0;
                            return (
                                <TableRow key={role._id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">{role.name}</Typography>
                                        {role.description && (
                                            <Typography variant="caption" color="text.secondary">
                                                {role.description}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={role.type}
                                            size="small"
                                            color={role.type === 'Internal' ? 'primary' : 'secondary'}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip label={role.level} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell align="center">
                                        <HasDefaultBadge count={widgetCount} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<DashboardIcon />}
                                            onClick={() => onEdit(role)}
                                        >
                                            Edit Default
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        )}
    </>
);

const EditorView = ({ role, onBack, onSaved }) => {
    const dispatch = useDispatch();
    const initial = useMemo(() => ({
        layout: Array.isArray(role.defaultDashboard?.layout) ? role.defaultDashboard.layout : [],
        widgets: (role.defaultDashboard?.widgets && typeof role.defaultDashboard.widgets === 'object')
            ? role.defaultDashboard.widgets
            : {},
    }), [role]);

    const [layout, setLayout] = useState(initial.layout);
    const [widgets, setWidgets] = useState(initial.widgets);
    const [isLocked, setIsLocked] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [rolePermissions, setRolePermissions] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setRolePermissions(null);
        dispatch(getRoleExpandedPermissions(role._id, (data) => {
            if (cancelled) return;
            if (data) {
                setRolePermissions(data.permissions || []);
            } else {
                setRolePermissions([]);
                dispatch(showAlert({ severity: 'error', message: 'Could not load role permissions; widget catalog will be empty.' }));
            }
        }));
        return () => { cancelled = true; };
    }, [role._id, dispatch]);

    const handleChange = useCallback(({ layout: nl, widgets: nw }) => {
        setLayout(nl);
        setWidgets(nw);
    }, []);

    const handleCommit = useCallback(() => {
        setIsDirty(true);
    }, []);

    const handleSave = () => {
        setSaving(true);
        const payload = {
            ...role,
            defaultDashboard: { layout, widgets },
        };
        delete payload._id;
        delete payload.createdAt;
        delete payload.updatedAt;
        delete payload.__v;

        dispatch(updateRole(role._id, payload, () => {
            setSaving(false);
            setIsDirty(false);
            dispatch(showAlert({ severity: 'success', message: `Default dashboard saved for ${role.name}` }));
            onSaved?.();
        }));
    };

    const handleDiscard = () => {
        setLayout(initial.layout);
        setWidgets(initial.widgets);
        setIsDirty(false);
    };

    return (
        <>
            <PageHeader
                title={`Default Dashboard — ${role.name}`}
                subtitle="Layout shown to users with this role until they personalize their own."
                actions={
                    <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
                        Back to Roles
                    </Button>
                }
            />

            <InfoNote severity="info">
                Preview shows widget placeholders, not live data. The "Add Widget" catalog is filtered to widgets this role's users can actually see.
                {rolePermissions !== null && rolePermissions.length === 0 && ' This role has no granular permissions — the widget catalog will be empty.'}
            </InfoNote>

            <EditorActions>
                <Button
                    variant={isLocked ? 'outlined' : 'contained'}
                    startIcon={isLocked ? <LockIcon /> : <LockOpenIcon />}
                    onClick={() => setIsLocked((v) => !v)}
                    color={isLocked ? 'inherit' : 'primary'}
                >
                    {isLocked ? 'Locked' : 'Editing'}
                </Button>
                <Button
                    variant="contained"
                    color="success"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={!isDirty || saving}
                >
                    {saving ? 'Saving...' : 'Save Default'}
                </Button>
                <Button onClick={handleDiscard} disabled={!isDirty || saving}>
                    Discard Changes
                </Button>
            </EditorActions>

            {rolePermissions === null ? (
                <LoadingRow>
                    <CircularProgress />
                </LoadingRow>
            ) : (
                <DashboardEditor
                    layout={layout}
                    widgets={widgets}
                    isLocked={isLocked}
                    permissions={rolePermissions}
                    onChange={handleChange}
                    onCommit={handleCommit}
                    onToggleLock={() => setIsLocked((v) => !v)}
                    previewMode
                    toolbarTitle={`${role.name} dashboard preview`}
                    showLockToggle={false}
                    emptyTitle="Empty default dashboard"
                    emptyBody='Click "Add Widget" above to start composing this role&apos;s default layout.'
                />
            )}
        </>
    );
};

const AdminRoleDashboards = () => {
    const dispatch = useDispatch();
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [editingRole, setEditingRole] = useState(null);

    const loadRoles = useCallback(() => {
        setLoading(true);
        dispatch(getRoles((data) => {
            setRoles(data || []);
            setLoading(false);
        }));
    }, [dispatch]);

    useEffect(() => { loadRoles(); }, [loadRoles]);

    const filteredRoles = useMemo(() => {
        let filtered = roles;
        if (typeFilter !== 'all') filtered = filtered.filter((r) => r.type === typeFilter);
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter((r) =>
                r.name?.toLowerCase().includes(term) || r.description?.toLowerCase().includes(term)
            );
        }
        return filtered;
    }, [roles, searchTerm, typeFilter]);

    return (
        <AdminPageContainer>
            {editingRole ? (
                <EditorView
                    role={editingRole}
                    onBack={() => setEditingRole(null)}
                    onSaved={() => { loadRoles(); }}
                />
            ) : (
                <RoleListView
                    roles={filteredRoles}
                    loading={loading}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    typeFilter={typeFilter}
                    setTypeFilter={setTypeFilter}
                    onEdit={setEditingRole}
                />
            )}
        </AdminPageContainer>
    );
};

export default AdminRoleDashboards;
